import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  Folder,
  FileText,
  Users,
  Building,
  Briefcase,
  FolderOpen,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  File,
  Loader
} from 'lucide-react';

/**
 * CRMAttachmentPicker - Sélecteur de pièces jointes depuis le CRM
 * Permet de sélectionner des documents depuis :
 * - CRM Client
 * - CRM Courtier  
 * - Annuaire (Compagnies)
 * - Documents Cabinet
 */
const CRMAttachmentPicker = ({ 
  isOpen, 
  onClose, 
  onSelectAttachments,
  backendUrl,
  authToken,
  preselectedClient  // Objet client pré-sélectionné {id, first_name, last_name, ...}
}) => {
  const { t } = useTranslation('admin');
  // Source active
  const [activeSource, setActiveSource] = useState('clients');
  
  // Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [searchEntityTerm, setSearchEntityTerm] = useState('');
  
  // Données
  const [clients, setClients] = useState([]);
  const [courtiers, setCourtiers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cabinetDocs, setCabinetDocs] = useState([]);
  const [cabinetFolders, setCabinetFolders] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 20;
  
  // Sélection courante
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityDocuments, setEntityDocuments] = useState([]);
  const [entityFolders, setEntityFolders] = useState([]);
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [selectedDocs, setSelectedDocs] = useState([]);
  
  // Loading
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Sources disponibles
  const sources = [
    { id: 'clients', label: t('docs.attach.crmClients'), icon: Users, color: 'blue' },
    { id: 'courtiers', label: t('docs.attach.crmCourtiers'), icon: Briefcase, color: 'green' },
    { id: 'companies', label: t('docs.attach.directoryCompanies'), icon: Building, color: 'purple' },
    { id: 'cabinet', label: t('docs.attach.cabinetDocs'), icon: FolderOpen, color: 'amber' }
  ];

  // Charger les entités selon la source
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchEntityTerm('');
      const hasPreselect = !!(preselectedClient && activeSource === 'clients');
      loadEntities(1, '', hasPreselect);
      // Si un client est pré-sélectionné, charger directement ses documents
      if (hasPreselect) {
        setSelectedEntity(preselectedClient);
        loadEntityDocuments(preselectedClient);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeSource]);

  // Recharger quand la page change
  useEffect(() => {
    if (isOpen && activeSource === 'clients') {
      loadEntities(currentPage, searchEntityTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadEntities = async (page = 1, searchTerm = '', keepSelection = false) => {
    setLoadingEntities(true);
    if (!keepSelection) {
      setSelectedEntity(null);
      setEntityDocuments([]);
    }
    
    try {
      let url = '';
      switch (activeSource) {
        case 'clients':
          url = `${backendUrl}/api/admin/users?role=client&limit=${ITEMS_PER_PAGE}&page=${page}`;
          if (searchTerm && searchTerm.length >= 2) {
            url += `&search=${encodeURIComponent(searchTerm)}`;
          }
          break;
        case 'courtiers':
          url = `${backendUrl}/api/admin/courtiers`;
          break;
        case 'companies':
          url = `${backendUrl}/api/admin/contact-directory/companies`;
          break;
        case 'cabinet':
          // Pour cabinet, on charge tous les documents (tous dossiers confondus)
          url = `${backendUrl}/api/admin/cabinet/documents?all_folders=true`;
          break;
        default:
          return;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        switch (activeSource) {
          case 'clients':
            // Les clients viennent de /api/admin/users avec format { users: [...], pagination: {...} }
            setClients(data.users || data.items || data || []);
            if (data.pagination) {
              setTotalPages(data.pagination.total_pages || 1);
              setTotalCount(data.pagination.total || 0);
            }
            break;
          case 'courtiers':
            setCourtiers(data.items || data || []);
            break;
          case 'companies':
            setCompanies(data || []);
            break;
          case 'cabinet':
            setCabinetDocs(data || []);
            // Charger aussi TOUS les dossiers (pour le regroupement par dossier)
            const foldersRes = await fetch(`${backendUrl}/api/admin/cabinet/folders?all_folders=true`, {
              headers: { 'Authorization': `Bearer ${authToken}` }
            });
            if (foldersRes.ok) {
              setCabinetFolders(await foldersRes.json());
            }
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('Erreur chargement entités:', error);
    } finally {
      setLoadingEntities(false);
    }
  };

  // Charger les documents d'une entité
  const loadEntityDocuments = async (entity) => {
    setSelectedEntity(entity);
    setLoadingDocs(true);
    setEntityFolders([]);
    setCollapsedFolders({});
    
    try {
      let url = '';
      switch (activeSource) {
        case 'clients':
          url = `${backendUrl}/api/client/${entity.id}/documents`;
          break;
        case 'courtiers':
          url = `${backendUrl}/api/courtier/${entity.id}/documents`;
          break;
        case 'companies':
          url = `${backendUrl}/api/admin/contact-directory/companies/${entity.id}/documents`;
          break;
        default:
          return;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEntityDocuments(data || []);
      }

      // Les clients organisent leurs documents en dossiers : les charger pour respecter l'arborescence
      if (activeSource === 'clients') {
        try {
          const foldersRes = await fetch(`${backendUrl}/api/client/${entity.id}/folders`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (foldersRes.ok) {
            setEntityFolders(await foldersRes.json() || []);
          }
        } catch (e) {
          console.error('Erreur chargement dossiers:', e);
        }
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Toggle sélection d'un document
  const toggleDocSelection = (doc, source) => {
    const docKey = `${source}-${doc.id}`;
    const isSelected = selectedDocs.some(d => d.key === docKey);
    
    if (isSelected) {
      setSelectedDocs(selectedDocs.filter(d => d.key !== docKey));
    } else {
      setSelectedDocs([...selectedDocs, {
        key: docKey,
        id: doc.id,
        name: doc.original_filename || doc.filename || doc.name,
        source: source,
        entityId: selectedEntity?.id,
        entityName: selectedEntity ? getEntityName(selectedEntity) : t('docs.attach.cabinetFallback'),
        size: doc.file_size || doc.size,
        contentType: doc.content_type || doc.mime_type
      }]);
    }
  };

  // Nom de l'entité
  const getEntityName = (entity) => {
    if (entity.first_name && entity.last_name) {
      return `${entity.first_name} ${entity.last_name}`;
    }
    return entity.name || entity.company || t('docs.attach.noName');
  };

  // Filtrer les entités
  const getFilteredEntities = () => {
    let entities = [];
    switch (activeSource) {
      case 'clients':
        entities = clients;
        break;
      case 'courtiers':
        entities = courtiers;
        break;
      case 'companies':
        entities = companies;
        break;
      default:
        return [];
    }

    if (!searchEntityTerm) return entities;
    
    const term = searchEntityTerm.toLowerCase();
    return entities.filter(e => {
      const name = getEntityName(e).toLowerCase();
      const email = (e.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  };

  // Filtrer les documents
  const getFilteredDocuments = () => {
    let docs = activeSource === 'cabinet' ? cabinetDocs : entityDocuments;
    
    if (!searchTerm) return docs;
    
    const term = searchTerm.toLowerCase();
    return docs.filter(d => {
      const name = (d.original_filename || d.filename || d.name || '').toLowerCase();
      return name.includes(term);
    });
  };

  // Confirmer la sélection
  const handleConfirm = () => {
    onSelectAttachments(selectedDocs);
    onClose();
  };

  // Regrouper les documents par dossier (clients uniquement)
  const buildFolderPath = (folder, foldersById) => {
    const parts = [folder.name];
    let current = folder;
    const guard = new Set([folder.id]);
    while (current.parent_folder_id && foldersById[current.parent_folder_id] && !guard.has(current.parent_folder_id)) {
      guard.add(current.parent_folder_id);
      current = foldersById[current.parent_folder_id];
      parts.unshift(current.name);
    }
    return parts.join(' / ');
  };

  const getGroupedDocuments = () => {
    const docs = getFilteredDocuments();
    const foldersList = activeSource === 'cabinet' ? cabinetFolders : entityFolders;
    const foldersById = {};
    foldersList.forEach(f => { foldersById[f.id] = f; });

    const groups = {};
    docs.forEach(doc => {
      const fid = doc.folder_id || '__none__';
      if (!groups[fid]) groups[fid] = [];
      groups[fid].push(doc);
    });

    const sections = [];
    // Dossiers connus (triés par chemin)
    foldersList
      .map(f => ({ folder: f, path: buildFolderPath(f, foldersById) }))
      .sort((a, b) => a.path.localeCompare(b.path))
      .forEach(({ folder, path }) => {
        if (groups[folder.id]) {
          sections.push({ id: folder.id, name: path, docs: groups[folder.id] });
          delete groups[folder.id];
        }
      });
    // Dossiers inconnus restants (folder_id sans dossier correspondant)
    Object.keys(groups).forEach(fid => {
      if (fid !== '__none__') {
        sections.push({ id: fid, name: t('docs.attach.otherFolder', 'Autre dossier'), docs: groups[fid] });
      }
    });
    // Sans dossier
    if (groups['__none__']) {
      sections.push({ id: '__none__', name: t('docs.attach.noFolder', 'Sans dossier'), docs: groups['__none__'] });
    }
    return sections;
  };

  const toggleFolderCollapse = (folderId) => {
    setCollapsedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  // Rendu d'un document (bouton sélectionnable)
  const renderDocButton = (doc) => {
    const docKey = `${activeSource}-${doc.id}`;
    const isSelected = selectedDocs.some(d => d.key === docKey);
    return (
      <button
        key={doc.id}
        data-testid={`crm-attach-doc-${doc.id}`}
        onClick={() => toggleDocSelection(doc, activeSource)}
        className={`flex items-center p-3 rounded-lg border transition-all text-left ${
          isSelected
            ? 'border-red-500 bg-red-50 ring-1 ring-red-500'
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
          isSelected ? 'bg-red-500' : 'bg-slate-100'
        }`}>
          {isSelected ? <Check className="w-4 h-4 text-white" /> : getFileIcon(doc)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">
            {doc.original_filename || doc.filename || doc.name}
          </p>
          <p className="text-xs text-slate-500">
            {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
          </p>
        </div>
      </button>
    );
  };

  // Icône de fichier
  const getFileIcon = (doc) => {
    const name = doc.original_filename || doc.filename || doc.name || '';
    const type = doc.content_type || doc.mime_type || '';
    
    if (type.includes('pdf') || name.endsWith('.pdf')) {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    if (type.includes('image') || /\.(jpg|jpeg|png|gif)$/i.test(name)) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    }
    if (type.includes('word') || /\.(doc|docx)$/i.test(name)) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    if (type.includes('excel') || /\.(xls|xlsx)$/i.test(name)) {
      return <FileText className="w-5 h-5 text-green-600" />;
    }
    return <File className="w-5 h-5 text-slate-500" />;
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1500] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {t('docs.attach.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - Sources */}
          <div className="w-56 border-r border-slate-200 bg-slate-50 p-3 flex-shrink-0">
            <p className="text-xs font-medium text-slate-500 uppercase mb-2 px-2">{t('docs.attach.sources')}</p>
            <div className="space-y-1">
              {sources.map(source => {
                const Icon = source.icon;
                const isActive = activeSource === source.id;
                return (
                  <button
                    key={source.id}
                    onClick={() => setActiveSource(source.id)}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive 
                        ? `bg-${source.color}-100 text-${source.color}-700 font-medium`
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? `text-${source.color}-600` : 'text-slate-400'}`} />
                    {source.label}
                  </button>
                );
              })}
            </div>

            {/* Documents sélectionnés */}
            {selectedDocs.length > 0 && (
              <div className="mt-6 pt-4 border-t border-slate-200">
                <p className="text-xs font-medium text-slate-500 uppercase mb-2 px-2">
                  {t('docs.attach.selected', { count: selectedDocs.length })}
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {selectedDocs.map(doc => (
                    <div 
                      key={doc.key}
                      className="flex items-center justify-between px-2 py-1.5 bg-white rounded border border-slate-200"
                    >
                      <span className="text-xs text-slate-700 truncate flex-1 mr-2">
                        {doc.name}
                      </span>
                      <button
                        onClick={() => setSelectedDocs(selectedDocs.filter(d => d.key !== doc.key))}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Liste des entités (sauf pour cabinet) */}
            {activeSource !== 'cabinet' && (
              <div className="w-72 border-r border-slate-200 flex flex-col">
                {/* Recherche entités */}
                <div className="p-3 border-b border-slate-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder={activeSource === 'clients' ? t('docs.attach.searchEntityClient') : activeSource === 'courtiers' ? t('docs.attach.searchEntityCourtier') : t('docs.attach.searchEntityCompany')}
                      value={searchEntityTerm}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchEntityTerm(newValue);
                        // Pour les clients, relancer la recherche côté serveur
                        if (activeSource === 'clients' && newValue.length >= 2) {
                          setCurrentPage(1);
                          loadEntities(1, newValue);
                        } else if (activeSource === 'clients' && newValue.length === 0) {
                          setCurrentPage(1);
                          loadEntities(1, '');
                        }
                      }}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  {/* Info pagination pour clients */}
                  {activeSource === 'clients' && totalCount > 0 && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {t('docs.attach.clientsFound', { count: totalCount })}
                    </p>
                  )}
                </div>

                {/* Liste */}
                <div className="flex-1 overflow-y-auto p-2">
                  {loadingEntities ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader className="w-6 h-6 text-slate-400 animate-spin" />
                    </div>
                  ) : getFilteredEntities().length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">{t('docs.attach.noResult')}</p>
                  ) : (
                    <div className="space-y-1">
                      {getFilteredEntities().map(entity => (
                        <button
                          key={entity.id}
                          onClick={() => loadEntityDocuments(entity)}
                          className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                            selectedEntity?.id === entity.id
                              ? 'bg-red-50 border border-red-200'
                              : 'hover:bg-slate-100'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {getEntityName(entity)}
                            </p>
                            {entity.email && (
                              <p className="text-xs text-slate-500 truncate">{entity.email}</p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination pour clients */}
                {activeSource === 'clients' && totalPages > 1 && (
                  <div className="p-2 border-t border-slate-200 flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-slate-600">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Documents */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Recherche documents */}
              <div className="p-3 border-b border-slate-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t('docs.attach.searchDoc')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>

              {/* Liste documents */}
              <div className="flex-1 overflow-y-auto p-3">
                {loadingDocs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 text-slate-400 animate-spin" />
                  </div>
                ) : activeSource !== 'cabinet' && !selectedEntity ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                    <Folder className="w-12 h-12 text-slate-300 mb-3" />
                    <p className="text-sm">{activeSource === 'clients' ? t('docs.attach.selectClientHint') : activeSource === 'courtiers' ? t('docs.attach.selectCourtierHint') : t('docs.attach.selectCompanyHint')}</p>
                  </div>
                ) : getFilteredDocuments().length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 text-slate-300 mb-3" />
                    <p className="text-sm">{t('docs.attach.noDocFound')}</p>
                  </div>
                ) : (
                  activeSource === 'clients' || activeSource === 'cabinet' ? (
                    <div className="space-y-3" data-testid="crm-attach-folders">
                      {getGroupedDocuments().map(section => {
                        const isCollapsed = collapsedFolders[section.id];
                        return (
                          <div key={section.id} className="border border-slate-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleFolderCollapse(section.id)}
                              data-testid={`crm-attach-folder-${section.id}`}
                              className="w-full flex items-center px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                            >
                              {isCollapsed ? (
                                <ChevronRight className="w-4 h-4 text-slate-400 mr-1.5 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-400 mr-1.5 flex-shrink-0" />
                              )}
                              <Folder className="w-4 h-4 text-amber-500 mr-2 flex-shrink-0" />
                              <span className="text-sm font-medium text-slate-700 truncate flex-1">{section.name}</span>
                              <span className="text-xs text-slate-400 ml-2">{section.docs.length}</span>
                            </button>
                            {!isCollapsed && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                                {section.docs.map(doc => renderDocButton(doc))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {getFilteredDocuments().map(doc => renderDocButton(doc))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
          <p className="text-sm text-slate-600">
            {t('docs.attach.docsSelected', { count: selectedDocs.length })}
          </p>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedDocs.length === 0}
              data-testid="crm-attach-confirm"
              className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('docs.attach.attach', { count: selectedDocs.length })}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CRMAttachmentPicker;
